import { useEffect, useRef } from 'react';

/**
 * useSequentialRunner - I run queued tasks strictly one after another, and let a caller pause, cancel
 * or clear whatever has not started yet. ONLY concerned with the ordering - what a task does, and
 * what failing means, are its own.
 *
 * **Spark's send queue, and nothing else.** A reader can line several prompts up faster than the
 * model answers, and each send has to reach the wire after the one before it or the conversation
 * arrives out of order. `useSparkQueue` wraps this with the prompts a reader can see.
 */

/**
 * `settle` resolves the promise `enqueue` returned *without* running the task. A dropped task
 * still has a caller awaiting it, and leaving that promise unsettled leaves a form's `onSubmit`
 * submitting for the rest of the session.
 */
type QueuedTask = {
  key: string;
  run: () => Promise<void>;
  settle: () => void;
};

/**
 * The queue and the flags are refs rather than state because they have to be
 * read and written inside one tick: two submits in the same frame each need to
 * see what the other just pushed, which a `useState` value would not show until
 * the next render.
 *
 * A task handles its own failures. Anything it throws rejects the promise
 * `enqueue` handed back and never reaches the runner, which carries on with
 * whatever is behind it - a caller that needs the rest abandoned calls `clear`
 * from its own error path.
 *
 * Nothing in flight can be stopped, by `clear`, `cancel` or `pause`: there is no
 * request to cancel one with in here. All three only reach what is still waiting.
 */
export const useSequentialRunner = ({
  onDrained,
}: {
  onDrained: () => void;
}) => {
  const queueRef = useRef<QueuedTask[]>([]);
  const isRunningRef = useRef(false);
  const isPausedRef = useRef(false);

  /*
    A drain outlives the render that started it, so the callback is held the way the queue and the
    flags are: a rerender mid-drain would otherwise be finished by a callback the consumer has
    already replaced.
  */
  const onDrainedRef = useRef(onDrained);

  useEffect(() => {
    onDrainedRef.current = onDrained;
  });

  const removeTasks = ({
    matches,
  }: {
    matches: (task: QueuedTask) => boolean;
  }) => {
    const removed = queueRef.current.filter(task => matches(task));

    queueRef.current = queueRef.current.filter(task => !matches(task));
    removed.forEach(task => {
      task.settle();
    });

    return removed;
  };

  /*
    Each step re-reads the queue rather than walking a snapshot, so tasks that arrive behind a
    long one belong to this same drain.
  */
  const runNext = async (): Promise<void> => {
    /*
      The pause is checked here as well as in `drain`: `drain` guards the *start* of a run, this
      guards every step of one, and a pause almost always arrives mid-flight. Returning leaves the
      untouched tasks queued and `onDrained` unreached, which is right - the queue is waiting, not
      finished.
    */
    if (isPausedRef.current) return;

    const next = queueRef.current.shift();

    if (!next) return;

    await next.run();

    return runNext();
  };

  const drain = async () => {
    if (isPausedRef.current) return;

    // Already draining: the recursion above re-reads the queue and will reach
    // whatever was just pushed.
    if (isRunningRef.current) return;

    isRunningRef.current = true;

    try {
      await runNext();
    } finally {
      /*
        In a `finally` so a throw cannot leave the flag set: every later `enqueue` would then
        return early and nothing would run again for the rest of the session.
      */
      isRunningRef.current = false;

      /*
        "The runner stopped" and "there is nothing left" are not the same statement - a pause
        returns early with tasks still queued, and anything pushed during the last step was turned
        away by the guard above. Checking here is also what makes `onDrained` mean empty rather
        than merely finished.
      */
      if (queueRef.current.length > 0) {
        void drain();
      } else {
        onDrainedRef.current();
      }
    }
  };

  return {
    clear: () => removeTasks({ matches: () => true }).length,

    pause: () => {
      isPausedRef.current = true;
    },

    /** Safe on a queue that was never paused, or that has nothing left in it. */
    resume: () => {
      isPausedRef.current = false;

      // Guarded, or resuming an empty queue would report it drained a second time.
      if (queueRef.current.length > 0) void drain();
    },

    /**
     * `false` means it was not waiting any more, almost always because it has already started,
     * which cannot be undone. A caller showing the queue leaves its own list alone in that case,
     * because the task still reports itself started and finished in the usual way.
     */
    cancel: ({ key }: { key: string }) =>
      removeTasks({ matches: task => task.key === key }).length > 0,

    /**
     * Resolves once *that* task has run, not once the queue is empty. Awaiting it in a form's
     * `onSubmit` is what carries a failed task out to the global error handler.
     *
     * `key` need only be unique among what is waiting.
     *
     * `index` inserts rather than appends, so a task pulled out to be edited goes back at the
     * position it was taken from and editing one does not reorder the rest. Past the end it clamps
     * there, which is the right answer for a queue that may have shortened meanwhile; below zero it
     * clamps to the head.
     */
    enqueue: async ({
      index,
      key,
      task,
    }: {
      index?: number;
      key: string;
      task: () => Promise<void>;
    }) =>
      new Promise<void>((resolve, reject) => {
        const queued = {
          key,
          /*
            Cancelling resolves rather than rejects: nothing went wrong, and a rejection would
            travel up through `onSubmit` and have the global handler report a dismissal as a
            failure.
          */
          settle: resolve,
          run: async () => {
            try {
              await task();
              resolve();
            } catch (error) {
              reject(error instanceof Error ? error : new Error(String(error)));
            }
          },
        };

        if (index === undefined) {
          queueRef.current.push(queued);
        } else {
          // Clamped because `splice` reads a negative index as an offset from the end, which is the
          // opposite of the head a caller counting back past the first position means by it.
          queueRef.current.splice(Math.max(0, index), 0, queued);
        }

        void drain();
      }),
  };
};
